import { CustomerEventType } from "@redotech/redo-customer-event-common/customer-event-definition";
import { CustomerCharacteristicType } from "@redotech/redo-customer-segments-common/marketing/segments/segment-types";
import { Provider } from "@redotech/redo-order-common/provider";
import { RegistrationRewardType } from "@redotech/redo-return-warranties-common/extended-warranty";
import { XorOfUnions } from "@redotech/util/type";
import {
  LoyaltyLionIntegrationTrigger,
  LoyaltyLionIntegrationTriggerKey,
  loyaltyLionIntegrationTriggers,
  RechargeIntegrationTrigger,
  RechargeIntegrationTriggerKey,
  rechargeIntegrationTriggers,
  YotpoIntegrationTrigger,
  YotpoIntegrationTriggerKey,
  yotpoIntegrationTriggers,
} from "./integration-triggers";
import { Integration } from "./integrations";
import { OmsTriggerKey } from "./oms-trigger-key";
import { RuleTriggerType } from "./rules/types";
import { SchemaType } from "./schemas/schemas";
import type { FlowTeam, FlowTeamSettings } from "./team-contracts";

export const categories = [
  "Order tracking",
  "Return tracking",
  "Return processing",
  "Compensation",
  "Restocking",
  "Withdrawal",
  "Try first",
  "Marketing",
  "Reviews",
  "Upsell",
  "Oms",
  "Checkout personalization",
  "Checkout package",
  "Post-purchase",
  "Tracking page",
  "Extended warranty",
  "Integration",
  "Recover",
  "Rules",
  "Custom Event",
  "HSA",
] as const;
export type Category = (typeof categories)[number];
export const Category = categories;

const categorySet = new Set<string>(categories);

export function isCategory(value: unknown): value is Category {
  return typeof value === "string" && categorySet.has(value);
}
export function categoryToSnakeCase(category: Category) {
  return category.toLowerCase().replace(" ", "_");
}
export function snakeCaseToCategory(snakeCase: string): Category {
  const lowercase = snakeCase.replace("_", " ");
  return (lowercase.charAt(0).toUpperCase() + lowercase.slice(1)) as Category;
}

export enum OrderTrackingTriggerKey {
  ORDER_CREATED = "order_created",
  ORDER_FULFILLED = "order_fulfilled",
  ORDER_PRE_TRANSIT = "order_pre_transit",
  ORDER_IN_TRANSIT = "order_in_transit",
  ORDER_OUT_FOR_DELIVERY = "order_out_for_delivery",
  ORDER_DELIVERED = "order_delivered",
  ORDER_AVAILABLE_FOR_PICKUP = "order_available_for_pickup",
  ORDER_AVAILABLE_FOR_PICKUP_CARRIER = "order_available_for_pickup_carrier",
  ORDER_STALLED_IN_TRANSIT = "order_stalled_in_transit",
  ORDER_STALLED_IN_FULFILLMENT = "order_stalled_in_fulfillment",
  ORDER_DELAYED = "order_delayed",
  ORDER_ARRIVING_EARLY = "order_arriving_early",
  ORDER_RETURN_TO_SENDER = "order_return_to_sender",
  ORDER_DELIVERY_ATTEMPTED = "order_delivery_attempted", // has this ever done anything??
  ORDER_DELIVERY_FAILURE = "order_delivery_failure",
  ORDER_SHIPMENT_CANCELLED = "order_shipment_cancelled",
  ORDER_SHIPMENT_ERROR = "order_shipment_error",
  POST_PURCHASE_UPSELL = "post_purchase_upsell",
}

export enum EwTriggerKey {
  EXTENDED_WARRANTY_PURCHASED = "extended_warranty_purchased",
}

export function isOrderTrackingTrigger(
  trigger: TriggerKey,
): trigger is OrderTrackingTriggerKey {
  return Object.values(OrderTrackingTriggerKey).includes(
    trigger as OrderTrackingTriggerKey,
  );
}

// Fires right at order creation, before merchant-side tag-adder automations
// (apps, scripts, integrations) have necessarily webhooked their updates into
// Mongo. Consumers that branch on `orderTags` near order creation should
// refetch from the provider instead of trusting persisted state.
export function isOrderCreationTrigger(trigger: TriggerKey): boolean {
  return trigger === OrderTrackingTriggerKey.ORDER_CREATED;
}

export enum ReturnTrackingTriggerKey {
  // easypost
  RETURN_REQUEST_CREATED = "return_created",
  RETURN_PRE_TRANSIT = "return_pre_transit",
  RETURN_IN_TRANSIT = "return_in_transit",
  RETURN_OUT_FOR_DELIVERY = "return_out_for_delivery",
  RETURN_DELIVERED = "return_delivered",
  RETURN_AVAILABLE_FOR_PICKUP = "return_available_for_pickup",
  RETURN_STALLED = "return_stalled",
  RETURN_DELAYED = "return_delayed",
  RETURN_ARRIVING_EARLY = "return_arriving_early",
  RETURN_RETURN_TO_SENDER = "return_return_to_sender",
  RETURN_DELIVERY_ATTEMPTED = "return_delivery_attempted",
  RETURN_DELIVERY_FAILURE = "return_delivery_failure",
  RETURN_SHIPMENT_CANCELLED = "return_shipment_cancelled",
  RETURN_SHIPMENT_ERROR = "return_shipment_error",
  RETURN_IN_REVIEW = "return_in_review",

  // redo
  // RETURN_RECEIVED = "return_received", // requirements unclear
  RETURN_APPROVED = "return_approved",
  RETURN_PROCESSED = "return_processed",
  RETURN_REJECTED = "return_rejected",
  // RETURN_ACCEPTED = "return_accepted", // requirements unclear
  // RETURN_EXCHANGE_ORDER_READY = "return_exchange_order_ready", // requirements unclear

  RETURN_PICKUP_RESCHEDULED_BY_MERCHANT = "return_pickup_rescheduled_by_merchant",
  RETURN_PICKUP_REMINDER = "return_pickup_reminder",
  RETURN_PICKUP_SUCCESS = "return_pickup_success",
  RETURN_PICKUP_FAILURE = "return_pickup_failure",
  RETURN_SHIPMENT_REMINDER = "return_shipment_reminder",
  RETURN_OUSTANDING_BALANCE = "return_outstanding_balance",
  RETURN_EXPIRED = "return_expired",
  RETURN_EXCHANGE_OUT_OF_STOCK = "return_exchange_out_of_stock",
  RETURN_EXCHANGE_COMPLETED = "return_exchange_completed",

  // Store credit
  STORE_CREDIT_CREATED = "store_credit_created",

  // External Return Shipping Providers
  BOXLESS_RETURNS_RMA_CREATED = "boxless_returns_rma_created",
}

/**
 * Trigger keys dispatchable through the OT v2 integration pipeline
 * (forward-order events and return-shipment events).
 */
export type TrackingDispatchTriggerKey =
  | OrderTrackingTriggerKey
  | ReturnTrackingTriggerKey;

export enum ReturnProcessingTriggerKey {
  RETURN_APPROVAL = "return_processing_approval",
  RETURN_IN_TRANSIT = "return_processing_in_transit",
  RETURN_DELIVERED = "return_processing_delivered",
}

export enum CompensationFlowTriggerKey {
  RETURN_APPROVAL = "compensation_flow_return_approval",
  RETURN_IN_TRANSIT = "compensation_flow_return_in_transit",
  RETURN_DELIVERED = "compensation_flow_return_delivered",
}

export enum RestockingFlowTriggerKey {
  RETURN_DELIVERED = "restocking_flow_return_delivered",
}

export enum WithdrawalFlowTriggerKey {
  RETURN_APPROVAL = "withdrawal_flow_return_approval",
  RETURN_IN_TRANSIT = "withdrawal_flow_return_in_transit",
  RETURN_DELIVERED = "withdrawal_flow_return_delivered",
}

export enum TnplTriggerKey {
  TRIAL_STARTED = "trial_started",
  ABOUT_TO_REAUTH_CUSTOMER = "about_to_reauth_customer",
  TRIAL_ITEMS_CANCELLED = "trial_items_cancelled",
  REAUTHORIZATION_FAILED = "reauthorization_failed",
  TRIAL_ENDED = "trial_ended",
}

export function isReturnTrackingTrigger(trigger: TriggerKey) {
  return Object.values(ReturnTrackingTriggerKey).includes(
    trigger as ReturnTrackingTriggerKey,
  );
}

export function isTnplTrackingTrigger(trigger: TriggerKey) {
  return Object.values(TnplTriggerKey).includes(trigger as TnplTriggerKey);
}

export function isEwTrigger(trigger: TriggerKey) {
  return Object.values(EwTriggerKey).includes(trigger as EwTriggerKey);
}

export enum MarketingTriggerKey {
  EMAIL_SIGNUP = "email_signup",
  EMAIL_SIGNUP_SHOPIFY = "email_signup_shopify",
  SMS_SIGNUP = "sms_confirmed",
  MARKETING_CAMPAIGN = "marketing_campaign",
  CART_ABANDONED = "cart_abandoned",
  BROWSE_ABANDONED = "browse_abandoned",
  CHECKOUT_ABANDONED = "checkout_abandoned",
  BIGCOMMERCE_CART_ABANDONED = "BIGCOMMERCE_CART_ABANDONED",
  COMMENTSOLD_CART_ABANDONED = "COMMENTSOLD_CART_ABANDONED",
  COMMENTSOLD_BROWSE_ABANDONED = "COMMENTSOLD_BROWSE_ABANDONED",
  COMMENTSOLD_CHECKOUT_ABANDONED = "COMMENTSOLD_CHECKOUT_ABANDONED",
  BIGCOMMERCE_CHECKOUT_ABANDONED = "BIGCOMMERCE_CHECKOUT_ABANDONED",
  BACK_IN_STOCK = "back_in_stock",
  CUSTOMER_GROUP_ENTERED = "customer_group_entered",
  CUSTOMER_GROUP_EXITED = "customer_group_exited",
  WARRANTY_REGISTRATION = "warranty_registration",
  LOW_INVENTORY = "low_inventory",
  DATE = "date",
  PRICE_DROP = "price_drop",
  PRODUCT_SUBSCRIPTION_CANCELLED = "product_subscription_cancelled",
  REFUND_RETURN_SUBMITTED = "refund_return_submitted",
  EXCHANGE_PROCESSED_WITH_CREDIT = "exchange_processed_with_credit",
  INBOUND_RECEIVED = "inbound_received",
  GIFT_CARD_BALANCE = "gift_card_balance",
}

export function isMarketingTrigger(trigger: TriggerKey) {
  return (
    Object.values(MarketingTriggerKey).includes(
      trigger as MarketingTriggerKey,
    ) || trigger === OrderTrackingTriggerKey.ORDER_CREATED
  );
}

/** Marketing-agent coordinator wake keys (ManualSignal, future Scheduled). Not playbook marketing. */
export enum MarketingAgentCustomKey {
  MANUAL = "manual",
}

export function isMarketingAgentCustomTrigger(trigger: TriggerKey) {
  return Object.values(MarketingAgentCustomKey).includes(
    trigger as MarketingAgentCustomKey,
  );
}

export function isRegistrationTrigger(trigger: TriggerKey) {
  return trigger === MarketingTriggerKey.WARRANTY_REGISTRATION;
}

export function isReturnsRecoverTrigger(trigger: TriggerKey) {
  // Triggers that route to the Returns queue (admin-only)
  return (
    trigger === MarketingTriggerKey.REFUND_RETURN_SUBMITTED ||
    trigger === MarketingTriggerKey.EXCHANGE_PROCESSED_WITH_CREDIT
  );
}

const marketingDateTriggerFields = [
  CustomerCharacteristicType.BIRTHDAY,
] as const;
marketingDateTriggerFields satisfies readonly CustomerCharacteristicType[];
export type MarketingDateTriggerField =
  (typeof marketingDateTriggerFields)[number];

export enum ReviewsTriggerKey {
  REVIEW_SUBMITTED = "review_submitted",
}

export enum CheckoutOptimizationTriggerKey {
  CHECKOUT_VISIT = "checkout_visit",
}

export enum CheckoutParcelTriggerKey {
  CHECKOUT_VISIT_PARCEL = "checkout_visit_parcel",
}

export enum PostPurchaseTriggerKey {
  ORDER_COMPLETED = "order_completed",
}

export enum TrackingPageTriggerKey {
  TRACKING_PAGE_VIEW = "tracking_page_view",
}

export enum CustomEventTriggerKey {
  CUSTOM_EVENT = "custom_event",
}

export enum HsaTriggerKey {
  // Paid with the HSA/FSA (Gale) payment method, items eligible without an LMN
  // — itemized receipt for record-keeping.
  HSA_ORDER_ELIGIBLE_RECEIPT = "hsa_order_eligible_receipt",
  // Dual-purpose items require a Letter of Medical Necessity (LMN); order is
  // bookmarked while the LMN is generated.
  HSA_ORDER_LMN_PENDING = "hsa_order_lmn_pending",
  // LMN returned: order created and the HSA/FSA card charged.
  HSA_ORDER_LMN_APPROVED = "hsa_order_lmn_approved",
}

export function isHsaTrigger(trigger: TriggerKey) {
  return Object.values(HsaTriggerKey).includes(trigger as HsaTriggerKey);
}

export type TriggerKey =
  | OrderTrackingTriggerKey
  | ReturnTrackingTriggerKey
  | ReturnProcessingTriggerKey
  | CompensationFlowTriggerKey
  | RestockingFlowTriggerKey
  | WithdrawalFlowTriggerKey
  | TnplTriggerKey
  | MarketingTriggerKey
  | MarketingAgentCustomKey
  | ReviewsTriggerKey
  | OmsTriggerKey
  | CheckoutOptimizationTriggerKey
  | CheckoutParcelTriggerKey
  | PostPurchaseTriggerKey
  | TrackingPageTriggerKey
  | EwTriggerKey
  | YotpoIntegrationTriggerKey
  | RechargeIntegrationTriggerKey
  | LoyaltyLionIntegrationTriggerKey
  | CustomEventTriggerKey
  | HsaTriggerKey
  | RuleTriggerType;

export interface BaseTrigger {
  category: Category;
  key: TriggerKey;
  displayName: string;
  schemaType: SchemaType;
  description: string;
}
export type AbandonmentKind = "cart" | "checkout" | "browse";

interface BaseMarketingTrigger extends BaseTrigger {
  category: "Marketing";
  // Set on provider-specific abandonment triggers; Shopify uses the generic ones.
  abandonment?: { provider: Provider; kind: AbandonmentKind };
}

export type MarketingEmailSignupTrigger = BaseMarketingTrigger & {
  key: MarketingTriggerKey.EMAIL_SIGNUP;
};

export type MarketingEmailSignupShopifyTrigger = BaseMarketingTrigger & {
  key: MarketingTriggerKey.EMAIL_SIGNUP_SHOPIFY;
};

export type MarketingSmsSignupTrigger = BaseMarketingTrigger & {
  key: MarketingTriggerKey.SMS_SIGNUP;
};

export type MarketingCampaignTrigger = BaseMarketingTrigger & {
  key: MarketingTriggerKey.MARKETING_CAMPAIGN;
};

export type MarketingCartAbandonedTrigger = BaseMarketingTrigger & {
  key: MarketingTriggerKey.CART_ABANDONED;
};

export type MarketingBrowseAbandonedTrigger = BaseMarketingTrigger & {
  key: MarketingTriggerKey.BROWSE_ABANDONED;
};

export type MarketingCheckoutAbandonedTrigger = BaseMarketingTrigger & {
  key: MarketingTriggerKey.CHECKOUT_ABANDONED;
};

export type MarketingBigcommerceCartAbandonedTrigger = BaseMarketingTrigger & {
  key: MarketingTriggerKey.BIGCOMMERCE_CART_ABANDONED;
};

export type MarketingCsCartAbandonedTrigger = BaseMarketingTrigger & {
  key: MarketingTriggerKey.COMMENTSOLD_CART_ABANDONED;
};

export type MarketingCsBrowseAbandonedTrigger = BaseMarketingTrigger & {
  key: MarketingTriggerKey.COMMENTSOLD_BROWSE_ABANDONED;
};

export type MarketingCsCheckoutAbandonedTrigger = BaseMarketingTrigger & {
  key: MarketingTriggerKey.COMMENTSOLD_CHECKOUT_ABANDONED;
};

export type MarketingBigcommerceCheckoutAbandonedTrigger =
  BaseMarketingTrigger & {
    key: MarketingTriggerKey.BIGCOMMERCE_CHECKOUT_ABANDONED;
  };

export type MarketingBackInStockTrigger = BaseMarketingTrigger & {
  key: MarketingTriggerKey.BACK_IN_STOCK;
};

export type MarketingCustomerGroupEnteredTrigger = BaseMarketingTrigger & {
  key: MarketingTriggerKey.CUSTOMER_GROUP_ENTERED;
};

export type MarketingCustomerGroupExitedTrigger = BaseMarketingTrigger & {
  key: MarketingTriggerKey.CUSTOMER_GROUP_EXITED;
};

export type MarketingWarrantyRegistrationTrigger = BaseMarketingTrigger & {
  key: MarketingTriggerKey.WARRANTY_REGISTRATION;
};

export type MarketingLowInventoryTrigger = BaseMarketingTrigger & {
  key: MarketingTriggerKey.LOW_INVENTORY;
};

export type MarketingDateTrigger = BaseMarketingTrigger & {
  key: MarketingTriggerKey.DATE;
};

export type MarketingPriceDropTrigger = BaseMarketingTrigger & {
  key: MarketingTriggerKey.PRICE_DROP;
};

export type MarketingProductSubscriptionCancelledTrigger =
  BaseMarketingTrigger & {
    key: MarketingTriggerKey.PRODUCT_SUBSCRIPTION_CANCELLED;
  };

export type MarketingRefundReturnSubmittedTrigger = BaseMarketingTrigger & {
  key: MarketingTriggerKey.REFUND_RETURN_SUBMITTED;
};

export type MarketingExchangeProcessedWithCreditTrigger =
  BaseMarketingTrigger & {
    key: MarketingTriggerKey.EXCHANGE_PROCESSED_WITH_CREDIT;
  };

export type MarketingInboundReceivedTrigger = BaseMarketingTrigger & {
  key: MarketingTriggerKey.INBOUND_RECEIVED;
};

export type MarketingGiftCardBalanceTrigger = BaseMarketingTrigger & {
  key: MarketingTriggerKey.GIFT_CARD_BALANCE;
};

export type MarketingTrigger =
  | MarketingEmailSignupTrigger
  | MarketingEmailSignupShopifyTrigger
  | MarketingSmsSignupTrigger
  | MarketingCampaignTrigger
  | MarketingCartAbandonedTrigger
  | MarketingBrowseAbandonedTrigger
  | MarketingCheckoutAbandonedTrigger
  | MarketingBigcommerceCartAbandonedTrigger
  | MarketingBigcommerceCheckoutAbandonedTrigger
  | MarketingCsCartAbandonedTrigger
  | MarketingCsBrowseAbandonedTrigger
  | MarketingCsCheckoutAbandonedTrigger
  | MarketingBackInStockTrigger
  | MarketingCustomerGroupEnteredTrigger
  | MarketingCustomerGroupExitedTrigger
  | MarketingWarrantyRegistrationTrigger
  | MarketingLowInventoryTrigger
  | MarketingDateTrigger
  | MarketingPriceDropTrigger
  | MarketingProductSubscriptionCancelledTrigger
  | MarketingRefundReturnSubmittedTrigger
  | MarketingExchangeProcessedWithCreditTrigger
  | MarketingInboundReceivedTrigger
  | MarketingGiftCardBalanceTrigger;

export interface OrderTrackingTrigger extends BaseTrigger {
  category: "Order tracking";
  key: OrderTrackingTriggerKey;
}
export interface ReturnTrackingTrigger extends BaseTrigger {
  category: "Return tracking";
  key: ReturnTrackingTriggerKey;
}

export interface BoxlessReturnsReturnTrackingTrigger extends BaseTrigger {
  category: "Return tracking";
  key: ReturnTrackingTriggerKey.BOXLESS_RETURNS_RMA_CREATED;
}

export interface ReturnProcessingTrigger extends BaseTrigger {
  category: "Return processing";
  key: ReturnProcessingTriggerKey;
}

export interface CompensationFlowTrigger extends BaseTrigger {
  category: "Compensation";
  key: CompensationFlowTriggerKey;
}

export interface RestockingFlowTrigger extends BaseTrigger {
  category: "Restocking";
  key: RestockingFlowTriggerKey;
}

export interface WithdrawalFlowTrigger extends BaseTrigger {
  category: "Withdrawal";
  key: WithdrawalFlowTriggerKey;
}

export interface TnplTrigger extends BaseTrigger {
  category: "Try first";
  key: TnplTriggerKey;
}
export interface ReviewsTrigger extends BaseTrigger {
  category: "Reviews";
  key: ReviewsTriggerKey;
}
export interface OmsTrigger extends BaseTrigger {
  category: "Oms";
  key: OmsTriggerKey;
}
export interface CheckoutOptimizationTrigger extends BaseTrigger {
  category: "Checkout personalization";
  key: CheckoutOptimizationTriggerKey;
}
export interface CheckoutParcelTrigger extends BaseTrigger {
  category: "Checkout package";
  key: CheckoutParcelTriggerKey;
}
export interface UpsellTrigger extends BaseTrigger {
  category: "Upsell";
  key: OrderTrackingTriggerKey.POST_PURCHASE_UPSELL;
}
export interface PostPurchaseTrigger extends BaseTrigger {
  category: "Post-purchase";
  key: PostPurchaseTriggerKey;
}
export interface TrackingPageTrigger extends BaseTrigger {
  category: "Tracking page";
  key: TrackingPageTriggerKey;
}
export interface EwTrigger extends BaseTrigger {
  category: "Extended warranty";
  key: EwTriggerKey;
}

export interface CustomEventTrigger extends BaseTrigger {
  category: "Custom Event";
  key: CustomEventTriggerKey;
}

export interface HsaTrigger extends BaseTrigger {
  category: "HSA";
  key: HsaTriggerKey;
}

export interface RulesTrigger extends BaseTrigger {
  category: "Rules";
  key: RuleTriggerType;
}

export type Trigger =
  | MarketingTrigger
  | OrderTrackingTrigger
  | ReturnTrackingTrigger
  | ReturnProcessingTrigger
  | CompensationFlowTrigger
  | RestockingFlowTrigger
  | WithdrawalFlowTrigger
  | TnplTrigger
  | ReviewsTrigger
  | OmsTrigger
  | CheckoutOptimizationTrigger
  | CheckoutParcelTrigger
  | UpsellTrigger
  | PostPurchaseTrigger
  | TrackingPageTrigger
  | EwTrigger
  | YotpoIntegrationTrigger
  | RechargeIntegrationTrigger
  | LoyaltyLionIntegrationTrigger
  | CustomEventTrigger
  | HsaTrigger
  | RulesTrigger;

// For every trigger key, there should be a trigger type
// in this file. This function expression enforces that.
// MarketingAgentCustomKey is coordinator-wake only (not a flow Trigger type).
// eslint-disable-next-line no-unused-expressions
() => {
  const a = "" as unknown as XorOfUnions<
    Trigger["key"],
    Exclude<TriggerKey, MarketingAgentCustomKey>
  >;
  a satisfies never;
};

// Exhaustive matching on SchemaType
type SchemaTypeConfig = {
  [K in SchemaType]: {
    category: Category;
    displayName: string;
    isEnabled: (settings: FlowTeamSettings) => boolean;
    /** Whether the Recover SMS step is offered; omitted means not supported. */
    supportsRecover?: boolean;
  };
};

export const schemaTypeConfigs: SchemaTypeConfig = {
  [SchemaType.ORDER_TRACKING]: {
    category: "Order tracking",
    displayName: "Order tracking",
    supportsRecover: true,
    isEnabled: (settings) => settings.orderTracking?.enabled ?? false,
  },
  [SchemaType.RETURN_TRACKING]: {
    category: "Return tracking",
    displayName: "Return tracking",
    isEnabled: (settings) => settings.returns?.enabled ?? false,
  },
  [SchemaType.RETURN_PROCESSING_FLOW]: {
    category: "Return processing",
    displayName: "Return processing",
    isEnabled: (settings) => settings.returns?.enabled ?? false,
  },
  [SchemaType.COMPENSATION_FLOW]: {
    category: "Compensation",
    displayName: "Compensation",
    isEnabled: (settings) => settings.returns?.enabled ?? false,
  },
  [SchemaType.RESTOCKING_FLOW]: {
    category: "Restocking",
    displayName: "Restocking",
    isEnabled: (settings) => settings.returns?.enabled ?? false,
  },
  [SchemaType.WITHDRAWAL_FLOW]: {
    category: "Withdrawal",
    displayName: "Withdrawal",
    // Global, non-editable flows — never surfaced as a configurable tile in
    // the merchant app, so this is always false.
    isEnabled: () => false,
  },
  [SchemaType.TNPL_TRACKING]: {
    category: "Try first",
    displayName: "Try first",
    isEnabled: (settings) => settings.tnpl?.enabled ?? false,
  },
  [SchemaType.EMAIL_MARKETING_SIGNUP]: {
    category: "Marketing",
    displayName: "Email marketing sign-up",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.SMS_MARKETING_SIGNUP]: {
    category: "Marketing",
    displayName: "SMS marketing sign-up",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.MARKETING_CART_ABANDONMENT]: {
    category: "Marketing",
    displayName: "Cart abandonment",
    supportsRecover: true,
    isEnabled: (settings) =>
      (settings.marketing?.enabled && !!settings.shopifyPixelId) ?? false,
  },
  [SchemaType.MARKETING_BROWSE_ABANDONMENT]: {
    category: "Marketing",
    displayName: "Browse abandonment",
    supportsRecover: true,
    isEnabled: (settings) =>
      (settings.marketing?.enabled && !!settings.shopifyPixelId) ?? false,
  },
  [SchemaType.MARKETING_CHECKOUT_ABANDONMENT]: {
    category: "Marketing",
    displayName: "Checkout abandonment",
    supportsRecover: true,
    isEnabled: (settings) =>
      (settings.marketing?.enabled && !!settings.shopifyPixelId) ?? false,
  },
  [SchemaType.MARKETING_BIGCOMMERCE_CART_ABANDONMENT]: {
    category: "Marketing",
    displayName: "BigCommerce cart abandonment",
    supportsRecover: true,
    isEnabled: (settings) => !!settings.marketing?.enabled,
  },
  [SchemaType.MARKETING_BIGCOMMERCE_CHECKOUT_ABANDONMENT]: {
    category: "Marketing",
    displayName: "BigCommerce checkout abandonment",
    supportsRecover: true,
    isEnabled: (settings) => !!settings.marketing?.enabled,
  },
  [SchemaType.MARKETING_COMMENTSOLD_CART_ABANDONMENT]: {
    category: "Marketing",
    displayName: "CommentSold cart abandonment",
    isEnabled: (settings) => !!settings.marketing?.enabled,
  },
  [SchemaType.MARKETING_COMMENTSOLD_BROWSE_ABANDONMENT]: {
    category: "Marketing",
    displayName: "CommentSold browse abandonment",
    isEnabled: (settings) => !!settings.marketing?.enabled,
  },
  [SchemaType.MARKETING_COMMENTSOLD_CHECKOUT_ABANDONMENT]: {
    category: "Marketing",
    displayName: "CommentSold checkout abandonment",
    isEnabled: (settings) => !!settings.marketing?.enabled,
  },
  [SchemaType.MARKETING_SEGMENT_MEMBERSHIP_CHANGE]: {
    category: "Marketing",
    displayName: "Segment membership change",
    supportsRecover: true,
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.REVIEWS]: {
    category: "Reviews",
    displayName: "Reviews",
    isEnabled: (settings) => settings.reviews?.enabled ?? false,
  },
  [SchemaType.OMS_NEW_FULFILLMENT_GROUP]: {
    category: "Oms",
    displayName: "New shipment",
    isEnabled: (settings) => settings.outboundLabels?.newOmsEnabled ?? false,
  },
  [SchemaType.OMS_ORDER_READY_FOR_PICKUP]: {
    category: "Oms",
    displayName: "OMS pickup order ready",
    isEnabled: (settings) =>
      (settings.outboundLabels?.newOmsEnabled &&
        settings.orderTracking?.enabled) ??
      false,
  },
  [SchemaType.OMS_LABEL_PURCHASED]: {
    category: "Oms",
    displayName: "OMS label purchased",
    isEnabled: (settings) =>
      (settings.outboundLabels?.newOmsEnabled &&
        settings.orderTracking?.enabled) ??
      false,
  },
  [SchemaType.OMS_FULFILLMENT_LABEL_PURCHASED]: {
    category: "Oms",
    displayName: "Fulfillment label purchased",
    isEnabled: (settings) => settings.outboundLabels?.newOmsEnabled ?? false,
  },
  [SchemaType.OMS_PACKING_SLIP_PRINTED]: {
    category: "Oms",
    displayName: "Packing slip printed",
    isEnabled: (settings) => settings.outboundLabels?.newOmsEnabled ?? false,
  },
  [SchemaType.ADDRESS_VERIFICATION_FAILED]: {
    category: "Oms",
    displayName: "OMS address verification failed",
    isEnabled: (settings) =>
      (settings.outboundLabels?.newOmsEnabled &&
        settings.orderTracking?.enabled) ??
      false,
  },
  [SchemaType.MARKETING_BACK_IN_STOCK]: {
    category: "Marketing",
    displayName: "Back in stock",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.MARKETING_LOW_INVENTORY]: {
    category: "Marketing",
    displayName: "Low inventory",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.MARKETING_WARRANTY_REGISTRATION]: {
    category: "Marketing",
    displayName: "Warranty registration",
    isEnabled: (settings) =>
      // TODO: Deprecated, remove after migration
      settings.warranties?.registrationRewardType ===
        RegistrationRewardType.EMAIL_DISCOUNT ||
      settings.warranties?.triggerMarketingFlow === true,
  },
  [SchemaType.MARKETING_CAMPAIGN]: {
    category: "Marketing",
    displayName: "Marketing campaign",
    isEnabled: () => false, // Not used
  },
  [SchemaType.MARKETING_EMAIL]: {
    category: "Marketing",
    displayName: "Marketing email",
    isEnabled: () => false, // Not used
  },
  [SchemaType.CHECKOUT_TREE]: {
    category: "Checkout personalization",
    displayName: "Checkout visit",
    isEnabled: () => false, // Not used
  },
  [SchemaType.RCO_CHECKOUT_PACKAGE]: {
    category: "Checkout package",
    displayName: "Checkout visit",
    isEnabled: () => false, // Not used
  },
  [SchemaType.MARKETING_DATE]: {
    category: "Marketing",
    displayName: "Marketing date",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.MARKETING_GIFT_CARD_BALANCE]: {
    category: "Marketing",
    displayName: "Gift card balance",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
    supportsRecover: true,
  },
  [SchemaType.MARKETING_PRICE_DROP]: {
    category: "Marketing",
    displayName: "Price drop",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.MARKETING_PRODUCT_SUBSCRIPTION_CANCELLED]: {
    category: "Marketing",
    displayName: "Product subscription cancelled",
    supportsRecover: true,
    // TODO: this won't work unless they have a Recharge integration. Maybe deserves its own setting.
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.REFUND_RETURN_SUBMITTED]: {
    category: "Marketing",
    displayName: "Refund return processed",
    supportsRecover: true,
    isEnabled: (settings) =>
      (settings.returns?.enabled && settings.marketing?.enabled) ?? false,
  },
  [SchemaType.EXCHANGE_PROCESSED_WITH_CREDIT]: {
    category: "Marketing",
    displayName: "Exchange processed with leftover credit",
    supportsRecover: true,
    isEnabled: (settings) =>
      (settings.returns?.enabled && settings.recover?.enabled) ?? false,
  },
  [SchemaType.POST_PURCHASE_FLOW]: {
    category: "Post-purchase",
    displayName: "Checkout completed",
    isEnabled: () => false, // Not used
  },
  [SchemaType.TRACKING_PAGE_FLOW]: {
    category: "Order tracking",
    displayName: "Tracking page view",
    isEnabled: () => false, // Not used - tracking pages have their own flow system
  },
  [SchemaType.PRODUCT_SUBSCRIPTION_UPCOMING_CHARGE]: {
    category: "Marketing",
    displayName: "Product subscription",
    // TODO: this won't work unless they have a Recharge integration. Maybe deserves its own setting.
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.EXTENDED_WARRANTY]: {
    category: "Extended warranty",
    displayName: "Extended warranty purchased",
    isEnabled: (settings) => settings.extendedWarranties?.enabled ?? false,
  },
  [SchemaType.YOTPO_LOYALTY_EXPIRATION_REMINDER]: {
    category: "Integration",
    displayName: "Yotpo loyalty expiration reminder",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.YOTPO_LOYALTY_POINTS_REMINDER]: {
    category: "Integration",
    displayName: "Yotpo loyalty points reminder",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.YOTPO_LOYALTY_REDEMPTION_REMINDER]: {
    category: "Integration",
    displayName: "Yotpo loyalty redemption reminder",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.YOTPO_LOYALTY_REDEMPTION_CREATED]: {
    category: "Integration",
    displayName: "Yotpo loyalty redemption created",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.YOTPO_LOYALTY_REFERRAL_COMPLETED]: {
    category: "Integration",
    displayName: "Yotpo loyalty referral completed",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.YOTPO_LOYALTY_REFERRAL_SHARED]: {
    category: "Integration",
    displayName: "Yotpo loyalty referral shared",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.YOTPO_LOYALTY_CUSTOMER_BIRTHDAY]: {
    category: "Integration",
    displayName: "Yotpo loyalty customer birthday",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.YOTPO_LOYALTY_OPT_IN]: {
    category: "Integration",
    displayName: "Yotpo loyalty opt in",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.YOTPO_LOYALTY_TIER_EARNED]: {
    category: "Integration",
    displayName: "Yotpo loyalty tier earned",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.YOTPO_LOYALTY_TIER_LOST]: {
    category: "Integration",
    displayName: "Yotpo loyalty tier lost",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.YOTPO_LOYALTY_POINTS_EARNED]: {
    category: "Integration",
    displayName: "Yotpo loyalty points earned",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.YOTPO_REVIEW_CREATED]: {
    category: "Integration",
    displayName: "Yotpo review created",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.LOYALTY_LION_ENROLLED]: {
    category: "Integration",
    displayName: "LoyaltyLion enrolled",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.LOYALTY_LION_REWARD_AVAILABLE]: {
    category: "Integration",
    displayName: "LoyaltyLion reward available",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.LOYALTY_LION_TIER_UPGRADE]: {
    category: "Integration",
    displayName: "LoyaltyLion tier upgrade",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.LOYALTY_LION_APPROACHING_TIER_UPGRADE]: {
    category: "Integration",
    displayName: "LoyaltyLion approaching tier upgrade",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.LOYALTY_LION_REFERRAL_COMPLETE]: {
    category: "Integration",
    displayName: "LoyaltyLion referral complete",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.LOYALTY_LION_RULE_COMPLETED]: {
    category: "Integration",
    displayName: "LoyaltyLion rule completed",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.CUSTOM_EVENT]: {
    category: "Custom Event",
    displayName: "Custom event",
    isEnabled: (settings) => settings.marketing?.enabled ?? false,
  },
  [SchemaType.RULES]: {
    category: "Rules",
    displayName: "Rules",
    isEnabled: (settings) => settings.support?.enabled ?? false,
  },
  [SchemaType.RULES_V3]: {
    category: "Rules",
    displayName: "Rules",
    isEnabled: (settings) => settings.support?.enabled ?? false,
  },
  [SchemaType.MARKETING_AGENT]: {
    category: "Marketing",
    displayName: "Marketing agent",
    isEnabled: () => false, // Never shown in UI - used programmatically only
  },
  [SchemaType.WORK_ORDER]: {
    category: "Oms",
    displayName: "Work order",
    isEnabled: () => false, // Used programmatically only
  },
  [SchemaType.HSA_ORDER]: {
    category: "HSA",
    displayName: "HSA",
    isEnabled: (settings) => settings.hsa?.enabled ?? false,
  },
};

export function isTriggerFeatureEnabledForTeam(
  trigger: Pick<Trigger, "key" | "schemaType">,
  settings: FlowTeamSettings,
): boolean {
  return (
    schemaTypeConfigs[trigger.schemaType].isEnabled(settings) ||
    (trigger.key === OrderTrackingTriggerKey.ORDER_CREATED &&
      settings.marketing?.enabled === true)
  );
}

export function categorySchemaPermutations(
  settings: FlowTeamSettings,
): { category: Category; schemaType: SchemaType; displayName: string }[] {
  const permutations: {
    category: Category;
    schemaType: SchemaType;
    displayName: string;
  }[] = [];

  // Add all enabled schema types
  for (const [schemaType, config] of Object.entries(schemaTypeConfigs)) {
    if (config.isEnabled(settings)) {
      permutations.push({
        category: config.category,
        schemaType: schemaType as SchemaType,
        displayName: config.displayName,
      });
    }
  }

  if (
    isTriggerFeatureEnabledForTeam(
      {
        key: OrderTrackingTriggerKey.ORDER_CREATED,
        schemaType: SchemaType.ORDER_TRACKING,
      },
      settings,
    ) &&
    !schemaTypeConfigs[SchemaType.ORDER_TRACKING].isEnabled(settings)
  ) {
    permutations.push({
      category: "Order tracking",
      schemaType: SchemaType.ORDER_TRACKING,
      displayName: "Order created",
    });
  }

  return permutations;
}

const orderTrackingTriggers: OrderTrackingTrigger[] = [
  {
    key: OrderTrackingTriggerKey.ORDER_CREATED,
    displayName: "Order created",
    description:
      "Fires immediately when customer completes purchase and order is created",
  },
  {
    key: OrderTrackingTriggerKey.ORDER_FULFILLED,
    displayName: "Order fulfilled",
    description:
      "Fires when merchant marks order as fulfilled (items packed and ready to ship)",
  },
  {
    key: OrderTrackingTriggerKey.ORDER_PRE_TRANSIT,
    displayName: "Order pre-transit",
    description:
      "Shipping label created but carrier hasn't picked up package yet",
  },
  {
    key: OrderTrackingTriggerKey.ORDER_IN_TRANSIT,
    displayName: "Order in-transit",
    description:
      "Carrier picks up package and it begins moving through shipping network",
  },
  {
    key: OrderTrackingTriggerKey.ORDER_OUT_FOR_DELIVERY,
    displayName: "Order out for delivery",
    description:
      "Package loaded on delivery vehicle for final delivery attempt",
  },
  {
    key: OrderTrackingTriggerKey.ORDER_DELIVERED,
    displayName: "Order delivered",
    description:
      "Carrier confirms package was successfully delivered to customer",
  },
  {
    key: OrderTrackingTriggerKey.ORDER_AVAILABLE_FOR_PICKUP,
    displayName: "Order available for pickup",
    description:
      "Pickup order is ready and customer can collect it from store/location",
  },
  {
    key: OrderTrackingTriggerKey.ORDER_AVAILABLE_FOR_PICKUP_CARRIER,
    displayName: "Order available for pickup (carrier)",
    description:
      "Carrier reports package is available for pickup at carrier facility",
  },
  {
    key: OrderTrackingTriggerKey.ORDER_STALLED_IN_TRANSIT,
    displayName: "Order stalled in transit",
    description:
      "Package hasn't moved locations for extended period during shipping",
  },
  {
    key: OrderTrackingTriggerKey.ORDER_STALLED_IN_FULFILLMENT,
    displayName: "Order stalled in fulfillment",
    description:
      "Order hasn't been fulfilled within expected timeframe after purchase",
  },
  {
    key: OrderTrackingTriggerKey.ORDER_DELAYED,
    displayName: "Order delayed",
    description: "Delivery date pushed back from original estimate",
  },
  {
    key: OrderTrackingTriggerKey.ORDER_ARRIVING_EARLY,
    displayName: "Order arriving early",
    description: "Package will arrive ahead of original delivery estimate",
  },
  {
    key: OrderTrackingTriggerKey.ORDER_RETURN_TO_SENDER,
    displayName: "Order return to sender",
    description: "Package being returned to merchant due to failed delivery",
  },
  {
    key: OrderTrackingTriggerKey.ORDER_DELIVERY_ATTEMPTED,
    displayName: "Order delivery attempted",
    description: "Carrier attempted delivery but customer wasn't available",
  },
  {
    key: OrderTrackingTriggerKey.ORDER_DELIVERY_FAILURE,
    displayName: "Order delivery failure",
    description:
      "Delivery permanently fails after multiple unsuccessful attempts",
  },
  {
    key: OrderTrackingTriggerKey.ORDER_SHIPMENT_CANCELLED,
    displayName: "Order shipment cancelled",
    description: "Shipment cancelled before delivery (by merchant or carrier)",
  },
  {
    key: OrderTrackingTriggerKey.ORDER_SHIPMENT_ERROR,
    displayName: "Order shipment error",
    description:
      "Carrier reports error with shipment requiring attention/resolution",
  },
].map((t) => ({
  ...t,
  category: "Order tracking" as const,
  schemaType: SchemaType.ORDER_TRACKING,
}));

export const returnTrackingTriggers: ReturnTrackingTrigger[] = [
  {
    key: ReturnTrackingTriggerKey.RETURN_REQUEST_CREATED,
    displayName: "Return request created",
    description:
      "Fires when customer submits a return request through the return portal",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_IN_TRANSIT,
    displayName: "Return in-transit",
    description:
      "Customer's return package picked up and moving toward merchant",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_OUT_FOR_DELIVERY,
    displayName: "Return out for delivery",
    description:
      "Return package loaded for final delivery to merchant facility",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_DELIVERED,
    displayName: "Return received", // renamed for UI clarity
    description: "The return package is delivered by the carrier",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_STALLED,
    displayName: "Return stalled in transit",
    description: "Return package hasn't moved locations for extended period",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_DELAYED,
    displayName: "Return delayed",
    description: "Return delivery to merchant delayed from original estimate",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_ARRIVING_EARLY,
    displayName: "Return arriving early",
    description: "Return package will arrive at merchant ahead of schedule",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_RETURN_TO_SENDER,
    displayName: "Return return to sender",
    description:
      "Return package being sent back to customer due to delivery issues",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_DELIVERY_ATTEMPTED,
    displayName: "Return delivery attempted",
    description: "Carrier attempted to deliver return to merchant but failed",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_DELIVERY_FAILURE,
    displayName: "Return delivery failure",
    description: "Return delivery permanently fails after multiple attempts",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_SHIPMENT_CANCELLED,
    displayName: "Return shipment cancelled",
    description: "Return shipment cancelled before reaching merchant",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_SHIPMENT_ERROR,
    displayName: "Return shipment error",
    description:
      "Carrier reports error with return shipment needing resolution",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_APPROVED,
    displayName: "Return approved",
    description:
      "Merchant approves return request after reviewing items/policy or return request is automatically approved",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_PROCESSED,
    displayName: "Return processed",
    description:
      "Merchant completes return processing or return is automatically processed (refund issued, exchange sent)",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_REJECTED,
    displayName: "Return rejected",
    description:
      "Merchant rejects return request due to policy violations or return request is automatically rejected",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_IN_REVIEW,
    displayName: "Return in review",
    description: "Returned items being inspected/reviewed by merchant",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_PICKUP_REMINDER,
    displayName: "Return pickup reminder",
    description: "Remind customer about scheduled return pickup",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_PICKUP_SUCCESS,
    displayName: "Return pickup success",
    description: "Return pickup successfully completed by carrier",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_PICKUP_FAILURE,
    displayName: "Return pickup failure",
    description: "Scheduled return pickup fails or customer unavailable",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_SHIPMENT_REMINDER,
    displayName: "Return shipment reminder",
    description: "Remind customer to ship their return if not yet sent",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_OUSTANDING_BALANCE,
    displayName: "Return outstanding balance",
    description:
      "Customer owes additional payment due to return/exchange price difference",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_EXPIRED,
    displayName: "Return expired",
    description:
      "Return window expires without customer completing return process",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_EXCHANGE_OUT_OF_STOCK,
    displayName: "Return exchange out of stock",
    description:
      "Requested exchange item becomes unavailable during return process",
  },
  {
    key: ReturnTrackingTriggerKey.RETURN_EXCHANGE_COMPLETED,
    displayName: "Exchange payment completed",
    description:
      "Customer has paid the outstanding balance for an exchange order",
  },
  {
    key: ReturnTrackingTriggerKey.STORE_CREDIT_CREATED,
    displayName: "Store credit created",
    description:
      "Store credit has been issued for a return (discount code, account credit, or gift card)",
  },
  {
    key: ReturnTrackingTriggerKey.BOXLESS_RETURNS_RMA_CREATED,
    displayName: "Boxless returns RMA created",
    description:
      "Return shipping label created but package hasn't been picked up yet",
  },
].map((t) => ({
  ...t,
  category: "Return tracking" as const,
  schemaType: SchemaType.RETURN_TRACKING,
}));

const tnplTriggers: TnplTrigger[] = [
  {
    key: TnplTriggerKey.TRIAL_STARTED,
    displayName: "Try First trial started",
    description: "Customer begins try-now-pay-later trial period",
  },
  {
    key: TnplTriggerKey.ABOUT_TO_REAUTH_CUSTOMER,
    displayName: "Try First about to reauthorize customer payment",
    description: "Customer payment needs reauthorization for trial items",
  },
  {
    key: TnplTriggerKey.REAUTHORIZATION_FAILED,
    displayName: "Try First reauthorization failed",
    description: "Customer payment reauthorization fails for trial items",
  },
  {
    key: TnplTriggerKey.TRIAL_ITEMS_CANCELLED,
    displayName: "Try First trial items cancelled",
    description: "Customer cancels specific items during trial period",
  },
  {
    key: TnplTriggerKey.TRIAL_ENDED,
    displayName: "Try First trial ended",
    description: "Try-first trial period concludes (items kept or returned)",
  },
].map((t) => ({
  ...t,
  category: "Try first" as const,
  schemaType: SchemaType.TNPL_TRACKING,
}));

export const returnProcessingTriggers: ReturnProcessingTrigger[] = [
  {
    key: ReturnProcessingTriggerKey.RETURN_APPROVAL,
    category: "Return processing",
    schemaType: SchemaType.RETURN_PROCESSING_FLOW,
    displayName: "Return approval",
    description: "Fires when a return is approved or automatically approved",
  },
  {
    key: ReturnProcessingTriggerKey.RETURN_IN_TRANSIT,
    category: "Return processing",
    schemaType: SchemaType.RETURN_PROCESSING_FLOW,
    displayName: "Return in-transit",
    description:
      "Fires when the customer's return package is picked up and in transit",
  },
  {
    key: ReturnProcessingTriggerKey.RETURN_DELIVERED,
    category: "Return processing",
    schemaType: SchemaType.RETURN_PROCESSING_FLOW,
    displayName: "Return delivered",
    description: "Fires when the returned package is delivered",
  },
];

export function isReturnProcessingTrigger(trigger: TriggerKey) {
  return Object.values(ReturnProcessingTriggerKey).includes(
    trigger as ReturnProcessingTriggerKey,
  );
}

export const compensationFlowTriggers: CompensationFlowTrigger[] = [
  {
    key: CompensationFlowTriggerKey.RETURN_APPROVAL,
    category: "Compensation",
    schemaType: SchemaType.COMPENSATION_FLOW,
    displayName: "Return approval",
    description:
      "Fires when a return is approved or automatically approved (compensation)",
  },
  {
    key: CompensationFlowTriggerKey.RETURN_IN_TRANSIT,
    category: "Compensation",
    schemaType: SchemaType.COMPENSATION_FLOW,
    displayName: "Return in-transit",
    description:
      "Fires when the customer's return package is picked up and in transit (compensation)",
  },
  {
    key: CompensationFlowTriggerKey.RETURN_DELIVERED,
    category: "Compensation",
    schemaType: SchemaType.COMPENSATION_FLOW,
    displayName: "Return delivered",
    description: "Fires when the returned package is delivered (compensation)",
  },
];

export function isCompensationFlowTrigger(trigger: TriggerKey) {
  return Object.values(CompensationFlowTriggerKey).includes(
    trigger as CompensationFlowTriggerKey,
  );
}

export const restockingFlowTriggers: RestockingFlowTrigger[] = [
  {
    key: RestockingFlowTriggerKey.RETURN_DELIVERED,
    category: "Restocking",
    schemaType: SchemaType.RESTOCKING_FLOW,
    displayName: "Return delivered",
    description: "Fires when the returned package is delivered (restocking)",
  },
];

export function isRestockingFlowTrigger(trigger: TriggerKey) {
  return Object.values(RestockingFlowTriggerKey).includes(
    trigger as RestockingFlowTriggerKey,
  );
}

export const withdrawalFlowTriggers: WithdrawalFlowTrigger[] = [
  {
    key: WithdrawalFlowTriggerKey.RETURN_APPROVAL,
    category: "Withdrawal",
    schemaType: SchemaType.WITHDRAWAL_FLOW,
    displayName: "Return approval",
    description:
      "Fires when a withdrawal is approved or automatically approved (withdrawal)",
  },
  {
    key: WithdrawalFlowTriggerKey.RETURN_IN_TRANSIT,
    category: "Withdrawal",
    schemaType: SchemaType.WITHDRAWAL_FLOW,
    displayName: "Return in-transit",
    description:
      "Fires when the customer's withdrawal package is picked up and in transit (withdrawal)",
  },
  {
    key: WithdrawalFlowTriggerKey.RETURN_DELIVERED,
    category: "Withdrawal",
    schemaType: SchemaType.WITHDRAWAL_FLOW,
    displayName: "Return delivered",
    description: "Fires when the withdrawal package is delivered (withdrawal)",
  },
];

export function isWithdrawalFlowTrigger(trigger: TriggerKey) {
  return Object.values(WithdrawalFlowTriggerKey).includes(
    trigger as WithdrawalFlowTriggerKey,
  );
}

const marketingTriggers: MarketingTrigger[] = [
  {
    key: MarketingTriggerKey.EMAIL_SIGNUP,
    displayName: "Marketing email signup",
    category: "Marketing",
    schemaType: SchemaType.EMAIL_MARKETING_SIGNUP,
    description:
      "Customer subscribes to email marketing via forms, popups, or widgets",
  },
  {
    key: MarketingTriggerKey.EMAIL_SIGNUP_SHOPIFY,
    displayName: "Marketing email signup through Shopify",
    category: "Marketing",
    schemaType: SchemaType.EMAIL_MARKETING_SIGNUP,
    description:
      "Customer subscribes via Shopify's native marketing acceptance checkbox",
  },
  {
    key: MarketingTriggerKey.SMS_SIGNUP,
    displayName: "Marketing SMS signup",
    category: "Marketing",
    schemaType: SchemaType.SMS_MARKETING_SIGNUP,
    description:
      "Customer subscribes to SMS marketing via forms, popups, or widgets",
  },
  {
    key: MarketingTriggerKey.CART_ABANDONED,
    displayName: "Shopify abandoned cart",
    category: "Marketing",
    schemaType: SchemaType.MARKETING_CART_ABANDONMENT,
    description:
      "Customer adds items to cart but doesn't start checkout within set timeframe",
  },
  {
    key: MarketingTriggerKey.BROWSE_ABANDONED,
    displayName: "Shopify abandoned browse",
    category: "Marketing",
    schemaType: SchemaType.MARKETING_BROWSE_ABANDONMENT,
    description:
      "Customer views products but doesn't add anything to cart within timeframe",
  },
  {
    key: MarketingTriggerKey.CHECKOUT_ABANDONED,
    displayName: "Shopify abandoned checkout",
    category: "Marketing",
    schemaType: SchemaType.MARKETING_CHECKOUT_ABANDONMENT,
    description:
      "Customer starts checkout process but doesn't complete payment",
  },
  {
    key: MarketingTriggerKey.BIGCOMMERCE_CART_ABANDONED,
    displayName: "BigCommerce abandoned cart",
    category: "Marketing",
    schemaType: SchemaType.MARKETING_BIGCOMMERCE_CART_ABANDONMENT,
    abandonment: { provider: Provider.BIGCOMMERCE, kind: "cart" },
    description:
      "BigCommerce customer adds items to cart but doesn't start checkout within set timeframe",
  },
  {
    key: MarketingTriggerKey.COMMENTSOLD_CART_ABANDONED,
    displayName: "CommentSold abandoned cart",
    category: "Marketing",
    schemaType: SchemaType.MARKETING_COMMENTSOLD_CART_ABANDONMENT,
    abandonment: { provider: Provider.COMMENTSOLD, kind: "cart" },
    description:
      "CommentSold customer adds items to cart but doesn't start checkout within set timeframe",
  },
  {
    key: MarketingTriggerKey.COMMENTSOLD_BROWSE_ABANDONED,
    displayName: "CommentSold abandoned browse",
    category: "Marketing",
    schemaType: SchemaType.MARKETING_COMMENTSOLD_BROWSE_ABANDONMENT,
    abandonment: { provider: Provider.COMMENTSOLD, kind: "browse" },
    description:
      "CommentSold customer views products but doesn't add anything to cart within timeframe",
  },
  {
    key: MarketingTriggerKey.COMMENTSOLD_CHECKOUT_ABANDONED,
    displayName: "CommentSold abandoned checkout",
    category: "Marketing",
    schemaType: SchemaType.MARKETING_COMMENTSOLD_CHECKOUT_ABANDONMENT,
    abandonment: { provider: Provider.COMMENTSOLD, kind: "checkout" },
    description:
      "CommentSold customer starts checkout process but doesn't complete payment",
  },
  {
    key: MarketingTriggerKey.BIGCOMMERCE_CHECKOUT_ABANDONED,
    displayName: "BigCommerce abandoned checkout",
    category: "Marketing",
    schemaType: SchemaType.MARKETING_BIGCOMMERCE_CHECKOUT_ABANDONMENT,
    abandonment: { provider: Provider.BIGCOMMERCE, kind: "checkout" },
    description:
      "BigCommerce customer starts checkout process but doesn't complete payment",
  },
  {
    key: MarketingTriggerKey.BACK_IN_STOCK,
    displayName: "Back in stock",
    category: "Marketing",
    schemaType: SchemaType.MARKETING_BACK_IN_STOCK,
    description:
      "Previously out-of-stock product variant becomes available again",
  },
  {
    key: MarketingTriggerKey.CUSTOMER_GROUP_ENTERED,
    displayName: "Segment entered",
    category: "Marketing",
    schemaType: SchemaType.MARKETING_SEGMENT_MEMBERSHIP_CHANGE,
    description:
      "Customer meets criteria and enters a defined marketing segment/group",
  },
  {
    key: MarketingTriggerKey.CUSTOMER_GROUP_EXITED,
    displayName: "Segment exited",
    category: "Marketing",
    schemaType: SchemaType.MARKETING_SEGMENT_MEMBERSHIP_CHANGE,
    description:
      "Customer no longer meets criteria and exits a marketing segment",
  },
  {
    key: MarketingTriggerKey.WARRANTY_REGISTRATION,
    displayName: "Warranty registration",
    category: "Marketing",
    schemaType: SchemaType.MARKETING_WARRANTY_REGISTRATION,
    description: "Customer registers product warranty and opts into marketing",
  },
  {
    key: MarketingTriggerKey.LOW_INVENTORY,
    displayName: "Low inventory",
    category: "Marketing",
    schemaType: SchemaType.MARKETING_LOW_INVENTORY,
    description: "Product variant inventory drops below threshold",
  },
  {
    key: MarketingTriggerKey.DATE,
    displayName: "Date",
    category: "Marketing",
    schemaType: SchemaType.MARKETING_DATE,
    description:
      "Fires on specific dates based on customer characteristics (e.g., birthdays)",
  },
  {
    key: MarketingTriggerKey.GIFT_CARD_BALANCE,
    displayName: "Gift card balance",
    category: "Marketing",
    schemaType: SchemaType.MARKETING_GIFT_CARD_BALANCE,
    description:
      "Fires when a customer holds an unused or partially-used gift card balance",
  },
  {
    key: MarketingTriggerKey.PRICE_DROP,
    displayName: "Price drop",
    category: "Marketing",
    schemaType: SchemaType.MARKETING_PRICE_DROP,
    description: "Product price drops below customer's watchlist threshold",
  },
  {
    key: MarketingTriggerKey.PRODUCT_SUBSCRIPTION_CANCELLED,
    displayName: "Product subscription cancelled",
    category: "Marketing",
    schemaType: SchemaType.MARKETING_PRODUCT_SUBSCRIPTION_CANCELLED,
    description:
      "Fires when a customer cancels their subscription to a product",
  },
  {
    key: MarketingTriggerKey.REFUND_RETURN_SUBMITTED,
    displayName: "Refund return processed",
    category: "Marketing",
    schemaType: SchemaType.REFUND_RETURN_SUBMITTED,
    description:
      "Fires when a customer's refund return has finished processing, with AI product recommendations.",
  },
  {
    key: MarketingTriggerKey.EXCHANGE_PROCESSED_WITH_CREDIT,
    displayName: "Exchange processed with leftover credit",
    category: "Marketing",
    schemaType: SchemaType.EXCHANGE_PROCESSED_WITH_CREDIT,
    description:
      "Fires when an exchange return is processed and the customer has leftover store credit to spend.",
  },
  {
    key: MarketingTriggerKey.INBOUND_RECEIVED,
    displayName: "Inbound message received",
    category: "Marketing",
    schemaType: SchemaType.MARKETING_AGENT,
    description: "Fires when an inbound message is received from a customer.",
  },
];

const reviewsTriggers: ReviewsTrigger[] = [
  {
    key: ReviewsTriggerKey.REVIEW_SUBMITTED,
    displayName: "Review submitted",
    category: "Reviews",
    schemaType: SchemaType.REVIEWS,
    description: "Customer submits product review",
  },
];

const hsaTriggers: HsaTrigger[] = [
  {
    key: HsaTriggerKey.HSA_ORDER_ELIGIBLE_RECEIPT,
    displayName: "HSA eligible item receipt (no LMN)",
    category: "HSA",
    schemaType: SchemaType.HSA_ORDER,
    description:
      "Customer paid with their HSA/FSA card and all items are eligible without a Letter of Medical Necessity — sends an itemized receipt.",
  },
  {
    key: HsaTriggerKey.HSA_ORDER_LMN_PENDING,
    displayName: "HSA order placed, LMN pending",
    category: "HSA",
    schemaType: SchemaType.HSA_ORDER,
    description:
      "Customer paid with their HSA/FSA card but dual-purpose items need a Letter of Medical Necessity — confirms the order was received while the LMN is generated.",
  },
  {
    key: HsaTriggerKey.HSA_ORDER_LMN_APPROVED,
    displayName: "HSA order LMN approved & charged",
    category: "HSA",
    schemaType: SchemaType.HSA_ORDER,
    description:
      "The Letter of Medical Necessity was approved, the order was created, and the customer's HSA/FSA card was charged.",
  },
];

const omsTriggers: OmsTrigger[] = [
  {
    key: OmsTriggerKey.NEW_FULFILLMENT_GROUP,
    displayName: "New shipment",
    category: "Oms",
    schemaType: SchemaType.OMS_NEW_FULFILLMENT_GROUP,
    description: "New shipment/fulfillment group created in OMS",
  },
  {
    key: OmsTriggerKey.ORDER_READY_FOR_PICKUP,
    displayName: "OMS pickup order ready",
    category: "Oms",
    schemaType: SchemaType.OMS_ORDER_READY_FOR_PICKUP,
    description: "Pickup order is ready at store location",
  },
  {
    key: OmsTriggerKey.LABEL_PURCHASED,
    displayName: "OMS label purchased",
    category: "Oms",
    schemaType: SchemaType.OMS_LABEL_PURCHASED,
    description:
      "A shipping label was purchased for the order during batch fulfillment",
  },
  {
    key: OmsTriggerKey.FULFILLMENT_LABEL_PURCHASED,
    displayName: "Fulfillment label purchased",
    category: "Oms",
    schemaType: SchemaType.OMS_FULFILLMENT_LABEL_PURCHASED,
    description:
      "A shipping label was purchased for a fulfillment group during batch fulfillment",
  },
  {
    key: OmsTriggerKey.PACKING_SLIP_PRINTED,
    displayName: "Packing slip printed",
    category: "Oms",
    schemaType: SchemaType.OMS_PACKING_SLIP_PRINTED,
    description:
      "A packing slip was printed for the shipment (fires again on every reprint)",
  },
  {
    key: OmsTriggerKey.ADDRESS_VERIFICATION_FAILED,
    displayName: "Address verification failed",
    category: "Oms",
    schemaType: SchemaType.ADDRESS_VERIFICATION_FAILED,
    description: "Shipping address fails verification checks",
  },
  {
    key: OmsTriggerKey.WORK_ORDER,
    displayName: "Work order",
    category: "Oms",
    schemaType: SchemaType.WORK_ORDER,
    description: "Work order flow trigger",
  },
];

export const omsAutomationListTriggerKeys = [
  OmsTriggerKey.NEW_FULFILLMENT_GROUP,
  OmsTriggerKey.FULFILLMENT_LABEL_PURCHASED,
  OmsTriggerKey.PACKING_SLIP_PRINTED,
] as const;

export const omsAutomationListTriggers: OmsTrigger[] = omsTriggers.filter(
  (trigger) =>
    (omsAutomationListTriggerKeys as readonly OmsTriggerKey[]).includes(
      trigger.key,
    ),
);

const checkoutOptimizationTriggers: CheckoutOptimizationTrigger[] = [
  {
    key: CheckoutOptimizationTriggerKey.CHECKOUT_VISIT,
    displayName: "Checkout visit",
    category: "Checkout personalization",
    schemaType: SchemaType.CHECKOUT_TREE,
    description: "Customer visits checkout page (for optimization flows)",
  },
];

const checkoutParcelTriggers: CheckoutParcelTrigger[] = [
  {
    key: CheckoutParcelTriggerKey.CHECKOUT_VISIT_PARCEL,
    displayName: "Checkout",
    category: "Checkout package",
    schemaType: SchemaType.RCO_CHECKOUT_PACKAGE,
    description: "Customer visits checkout with parcel/package enhancements",
  },
];

const postPurchaseTriggers: PostPurchaseTrigger[] = [
  {
    key: PostPurchaseTriggerKey.ORDER_COMPLETED,
    displayName: "Order completed",
    category: "Post-purchase",
    schemaType: SchemaType.POST_PURCHASE_FLOW,
    description: "Fires when checkout/purchase process is fully completed",
  },
];

const trackingPageTriggers: TrackingPageTrigger[] = [
  {
    key: TrackingPageTriggerKey.TRACKING_PAGE_VIEW,
    displayName: "Tracking page view",
    category: "Tracking page",
    schemaType: SchemaType.TRACKING_PAGE_FLOW,
    description: "Fires when a customer views the order tracking page",
  },
];

const ewTriggers: EwTrigger[] = [
  {
    key: EwTriggerKey.EXTENDED_WARRANTY_PURCHASED,
    displayName: "Extended warranty purchased",
    category: "Extended warranty",
    schemaType: SchemaType.EXTENDED_WARRANTY,
    description: "Customer purchases extended warranty for their product",
  },
];

const customEventTriggers: CustomEventTrigger[] = [
  {
    key: CustomEventTriggerKey.CUSTOM_EVENT,
    displayName: "Custom event",
    category: "Custom Event",
    schemaType: SchemaType.CUSTOM_EVENT,
    description:
      "Fires when a custom event is triggered via API with merchant-defined event names and properties",
  },
];

export const rulesTriggers: RulesTrigger[] = [
  {
    key: RuleTriggerType.CONVERSATION_CREATED,
    displayName: "Conversation created",
    category: "Rules",
    schemaType: SchemaType.RULES,
    description: "Fires when a new conversation is created",
  },
  {
    key: RuleTriggerType.CONVERSATION_MESSAGE_CREATED,
    displayName: "New customer message",
    category: "Rules",
    schemaType: SchemaType.RULES,
    description: "Fires when a new customer message is added to a conversation",
  },
  {
    key: RuleTriggerType.MERCHANT_MESSAGE_CREATED,
    displayName: "New merchant message",
    category: "Rules",
    schemaType: SchemaType.RULES,
    description: "Fires when a new merchant message is added to a conversation",
  },
  {
    key: RuleTriggerType.MESSAGE_RECEIVED,
    displayName: "Message received",
    category: "Rules",
    schemaType: SchemaType.RULES,
    description: "Fires when a message is received in a conversation",
  },
  {
    key: RuleTriggerType.CONVERSATION_STATUS_CHANGED,
    displayName: "Conversation status changed",
    category: "Rules",
    schemaType: SchemaType.RULES,
    description: "Fires when a conversation's status changes",
  },
  {
    key: RuleTriggerType.TAG_ADDED,
    displayName: "Tag added",
    category: "Rules",
    schemaType: SchemaType.RULES,
    description: "Fires when a tag is added to a conversation",
  },
  {
    key: RuleTriggerType.ASSIGNMENT_CHANGED,
    displayName: "Assignment changed",
    category: "Rules",
    schemaType: SchemaType.RULES,
    description: "Fires when a conversation's assignment changes",
  },
  {
    key: RuleTriggerType.CHAT_CONVERTED_TO_EMAIL,
    displayName: "Chat converted to email",
    category: "Rules",
    schemaType: SchemaType.RULES,
    description: "Fires when a chat conversation is converted to email",
  },
];

const rulesV3OnlyTriggers: RulesTrigger[] = [
  {
    key: RuleTriggerType.CHAT_HANDED_OFF_TO_AGENT,
    displayName: "Chat handed off to agent",
    category: "Rules",
    schemaType: SchemaType.RULES_V3,
    description:
      "Fires when the chat automation hands a conversation to a human agent",
  },
  {
    key: RuleTriggerType.TAG_REMOVED,
    displayName: "Tag removed",
    category: "Rules",
    schemaType: SchemaType.RULES_V3,
    description: "Fires when a tag is removed from a conversation",
  },
  {
    key: RuleTriggerType.CONVERSATION_PRIORITY_CHANGED,
    displayName: "Priority changed",
    category: "Rules",
    schemaType: SchemaType.RULES_V3,
    description: "Fires when a conversation's priority changes",
  },
  {
    key: RuleTriggerType.CONVERSATION_READ,
    displayName: "Conversation read",
    category: "Rules",
    schemaType: SchemaType.RULES_V3,
    description: "Fires when a conversation is read",
  },
  {
    key: RuleTriggerType.THREAD_CREATED,
    displayName: "Thread created",
    category: "Rules",
    schemaType: SchemaType.RULES_V3,
    description: "Fires when a new thread is created on a conversation",
  },
  {
    key: RuleTriggerType.CONVERSATION_INSIGHTS_GENERATED,
    displayName: "Conversation insights generated",
    category: "Rules",
    schemaType: SchemaType.RULES_V3,
    description:
      "Fires when AI conversation insights (including customer sentiment) are generated. Use this for rules that depend on message sentiment, which isn't available until insights are generated.",
  },
];

export const rulesV3Triggers: RulesTrigger[] = [
  ...rulesTriggers.map((t) => ({
    ...t,
    category: "Rules" as const,
    schemaType: SchemaType.RULES_V3,
  })),
  ...rulesV3OnlyTriggers,
];

export const triggers = [
  ...orderTrackingTriggers,
  ...returnTrackingTriggers,
  ...returnProcessingTriggers,
  ...compensationFlowTriggers,
  ...restockingFlowTriggers,
  ...tnplTriggers,
  ...marketingTriggers,
  ...reviewsTriggers,
  ...hsaTriggers,
  ...omsTriggers,
  ...checkoutOptimizationTriggers,
  ...checkoutParcelTriggers,
  ...postPurchaseTriggers,
  ...trackingPageTriggers,
  ...ewTriggers,
  ...yotpoIntegrationTriggers,
  ...rechargeIntegrationTriggers,
  ...loyaltyLionIntegrationTriggers,
  ...customEventTriggers,
  ...rulesTriggers,
  ...rulesV3Triggers,
];

export function getTriggerDisplayName(
  key: TriggerKey | undefined,
): string | undefined {
  return triggers.find((t) => t.key === key)?.displayName;
}

export function getSchemaTypeDisplayName(
  schemaType: SchemaType | undefined,
): string | undefined {
  if (!schemaType) {
    return undefined;
  }
  return schemaTypeConfigs[schemaType]?.displayName;
}

/**
 * CommentSold schema types and their Shopify equivalents.
 * Used for provider detection, prompt fallback, and event-type mapping.
 */
const commentsoldSchemaMap: Record<
  string,
  { shopifyEquivalent: SchemaType; eventType: CustomerEventType }
> = {
  [SchemaType.MARKETING_COMMENTSOLD_CART_ABANDONMENT]: {
    shopifyEquivalent: SchemaType.MARKETING_CART_ABANDONMENT,
    eventType: CustomerEventType.PRODUCT_ADDED_TO_CART,
  },
  [SchemaType.MARKETING_COMMENTSOLD_BROWSE_ABANDONMENT]: {
    shopifyEquivalent: SchemaType.MARKETING_BROWSE_ABANDONMENT,
    eventType: CustomerEventType.PRODUCT_VIEWED,
  },
  [SchemaType.MARKETING_COMMENTSOLD_CHECKOUT_ABANDONMENT]: {
    shopifyEquivalent: SchemaType.MARKETING_CHECKOUT_ABANDONMENT,
    eventType: CustomerEventType.CHECKOUT_STARTED,
  },
};

export function isCommentsoldSchemaType(schemaType: SchemaType): boolean {
  return schemaType in commentsoldSchemaMap;
}

// BigCommerce schema types and their Shopify equivalents; see commentsoldSchemaMap.
const bigcommerceSchemaMap: Record<
  string,
  { shopifyEquivalent: SchemaType; eventType: CustomerEventType }
> = {
  [SchemaType.MARKETING_BIGCOMMERCE_CART_ABANDONMENT]: {
    shopifyEquivalent: SchemaType.MARKETING_CART_ABANDONMENT,
    eventType: CustomerEventType.PRODUCT_ADDED_TO_CART,
  },
  [SchemaType.MARKETING_BIGCOMMERCE_CHECKOUT_ABANDONMENT]: {
    shopifyEquivalent: SchemaType.MARKETING_CHECKOUT_ABANDONMENT,
    eventType: CustomerEventType.CHECKOUT_STARTED,
  },
};

export function isBigcommerceSchemaType(schemaType: SchemaType): boolean {
  return schemaType in bigcommerceSchemaMap;
}

export function providerFromSchemaType(schemaType: SchemaType): Provider {
  if (isCommentsoldSchemaType(schemaType)) {
    return Provider.COMMENTSOLD;
  }
  if (isBigcommerceSchemaType(schemaType)) {
    return Provider.BIGCOMMERCE;
  }
  return Provider.SHOPIFY;
}

export function getShopifyEquivalentSchemaType(
  schemaType: SchemaType,
): SchemaType | undefined {
  return (
    commentsoldSchemaMap[schemaType]?.shopifyEquivalent ??
    bigcommerceSchemaMap[schemaType]?.shopifyEquivalent
  );
}

/**
 * Returns the primary CustomerEventType for a schema type.
 * Works for both CommentSold and Shopify schema types.
 * CommentSold types are resolved via commentsoldSchemaMap; base types are here.
 */
const baseSchemaEventTypes: Partial<Record<SchemaType, CustomerEventType>> = {
  [SchemaType.MARKETING_CART_ABANDONMENT]:
    CustomerEventType.PRODUCT_ADDED_TO_CART,
  [SchemaType.MARKETING_BROWSE_ABANDONMENT]: CustomerEventType.PRODUCT_VIEWED,
  [SchemaType.MARKETING_CHECKOUT_ABANDONMENT]:
    CustomerEventType.CHECKOUT_STARTED,
};

export function getSchemaTypeEventType(
  schemaType: SchemaType,
): CustomerEventType | undefined {
  return (
    commentsoldSchemaMap[schemaType]?.eventType ??
    bigcommerceSchemaMap[schemaType]?.eventType ??
    baseSchemaEventTypes[schemaType]
  );
}

/**
 * Maps a SchemaType directly to its MarketingTriggerKey.
 * Derived from the `marketingTriggers` array, so new providers register here
 * automatically just by adding their trigger definitions.
 */
const schemaTypeToTriggerKey: Partial<Record<SchemaType, MarketingTriggerKey>> =
  Object.fromEntries(
    marketingTriggers
      .filter(
        (
          t,
        ): t is Extract<
          (typeof marketingTriggers)[number],
          { schemaType: SchemaType }
        > => "schemaType" in t,
      )
      .map((t) => [t.schemaType, t.key]),
  );

export function getTriggerKeyForSchemaType(
  schemaType: SchemaType,
): MarketingTriggerKey | undefined {
  return schemaTypeToTriggerKey[schemaType];
}

// Generic Shopify keys, used for providers without their own abandonment trigger.
const genericAbandonmentTriggerKey: Record<
  "cart" | "checkout",
  MarketingTriggerKey
> = {
  cart: MarketingTriggerKey.CART_ABANDONED,
  checkout: MarketingTriggerKey.CHECKOUT_ABANDONED,
};

export function getAbandonmentTriggerKey(
  provider: Provider | undefined,
  kind: "cart" | "checkout",
): MarketingTriggerKey {
  const definition = marketingTriggers.find(
    (definition) =>
      definition.abandonment?.provider === provider &&
      definition.abandonment?.kind === kind,
  );
  return definition?.key ?? genericAbandonmentTriggerKey[kind];
}

export const HIDDEN_MARKETING_TRIGGER_KEYS = new Set<TriggerKey>([
  ReturnTrackingTriggerKey.BOXLESS_RETURNS_RMA_CREATED,
]);

export function getEnabledMarketingTriggers(
  team: FlowTeam,
  enabledIntegrations: Record<Integration, boolean>,
  availableProviders: Provider[],
) {
  const hasAnyIntegration = Object.values(enabledIntegrations).some(Boolean);

  const categories: Trigger["category"][] = [];
  if (team.settings.orderTracking?.enabled) {
    categories.push("Order tracking");
  }
  if (team.settings.returns?.enabled) {
    categories.push("Return tracking");
  }
  if (team.settings.marketing?.enabled) {
    categories.push("Marketing");
  }
  if (team.settings.reviews?.enabled) {
    categories.push("Reviews");
  }
  if (team.settings.tnpl?.enabled) {
    categories.push("Try first");
  }
  if (team.settings.extendedWarranties?.enabled) {
    categories.push("Extended warranty");
  }
  if (team.settings.marketing?.enabled && hasAnyIntegration) {
    categories.push("Integration");
  }
  if (team.settings.marketing?.enabled) {
    categories.push("Custom Event");
  }
  if (team.settings.hsa?.enabled) {
    categories.push("HSA");
  }
  // This function is used for marketing automations, so we don't need to
  // include checkout personalization triggers here.
  const enabledTriggers = triggers.filter((trigger) =>
    categories.includes(trigger.category),
  );

  if (
    isTriggerFeatureEnabledForTeam(
      {
        key: OrderTrackingTriggerKey.ORDER_CREATED,
        schemaType: SchemaType.ORDER_TRACKING,
      },
      team.settings,
    ) &&
    !schemaTypeConfigs[SchemaType.ORDER_TRACKING].isEnabled(team.settings)
  ) {
    enabledTriggers.push({
      key: OrderTrackingTriggerKey.ORDER_CREATED,
      displayName: "Order created",
      category: "Order tracking",
      schemaType: SchemaType.ORDER_TRACKING,
      description:
        "Fires immediately when customer completes purchase and order is created",
    });
  }

  const filteredTriggers = enabledTriggers.filter((trigger) => {
    if (trigger.category === "Marketing" && trigger.abandonment) {
      return availableProviders.includes(trigger.abandonment.provider);
    }

    if (trigger.category === "Integration") {
      return enabledIntegrations[trigger.integration];
    }

    return true;
  });

  return filteredTriggers;
}

export function getVisibleMarketingTriggers(
  team: FlowTeam,
  enabledIntegrations: Record<Integration, boolean>,
  availableProviders: Provider[],
) {
  return getEnabledMarketingTriggers(
    team,
    enabledIntegrations,
    availableProviders,
  ).filter((trigger) => !HIDDEN_MARKETING_TRIGGER_KEYS.has(trigger.key));
}

export function getTriggerName(key: TriggerKey) {
  return triggers.find((t) => t.key === key)?.displayName;
}
